import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Download, FileText, Loader2 } from 'lucide-react';
import { format as formatDate } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ConciliacaoResponseDto, ConciliacaoFilterDto, DivergenciaUnificadaDto } from '@/services/conciliacao-fiscal';
import { useLazyExecutarConciliacaoQuery } from '@/services/conciliacao-fiscal';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: ConciliacaoResponseDto;
  currentFilters?: ConciliacaoFilterDto;
  section?: 'summary' | 'unified';
}

export default function ExportDialog({ open, onOpenChange, data, currentFilters }: ExportDialogProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [fetchedRecords, setFetchedRecords] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [executarConciliacao] = useLazyExecutarConciliacaoQuery();

  const generateFilename = () => {
    const timestamp = formatDate(new Date(), 'yyyyMMdd_HHmmss');
    return `conciliacao_fiscal_${timestamp}.csv`;
  };

  // Helper functions for CSV generation
  const escapeCSVField = (field: any): string => {
    if (field === null || field === undefined) return '';
    const str = String(field);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const convertDivergenciaToCSV = (divergencia: DivergenciaUnificadaDto): string[] => {
    // Campos base sempre presentes (mesma ordem da tabela)
    const tipo = escapeCSVField(divergencia.tipo);
    const loja = escapeCSVField(divergencia.loja);
    const caixa = escapeCSVField(divergencia.caixa);
    const cupom = escapeCSVField(divergencia.cupom);

    // Migrate - statusValidatorPulos para pulo, statusValidator para outros
    const migrate = divergencia.tipo === 'PULO_NUMERACAO'
      ? escapeCSVField(divergencia.statusValidatorPulos || '-')
      : escapeCSVField(divergencia.statusValidator || '-');

    // VAR - statusDoc001Calculado com fallback para statusDoc001
    const var_ = escapeCSVField(divergencia.statusDoc001Calculado || divergencia.statusDoc001 || '-');

    // Logix - inutilizadoLogix
    const logix = escapeCSVField(divergencia.inutilizadoLogix ? 'Sim' : 'Não');

    // GFT - statusGftLivro
    const gft = escapeCSVField(divergencia.statusGftLivro || '-');

    // Data
    const data = escapeCSVField(formatDate(new Date(divergencia.data), 'dd/MM/yyyy', { locale: ptBR }));

    // Campos adicionais específicos por tipo (sempre 12 campos para manter estrutura consistente)
    let camposAdicionais: string[] = Array(12).fill('');

    switch (divergencia.tipo) {
      case 'PULO_NUMERACAO':
      case 'PULO_NUMERACAO_GFT':
        camposAdicionais[0] = escapeCSVField(divergencia.cupomAnterior || '');
        camposAdicionais[1] = escapeCSVField(divergencia.proximoCupom || '');
        camposAdicionais[2] = escapeCSVField(divergencia.tamanhoPulo || '');
        camposAdicionais[3] = escapeCSVField(divergencia.statusSimplificado || '');
        camposAdicionais[4] = escapeCSVField(divergencia.cnpjLoja || '');
        break;
      case 'EXCLUSIVO_DOC001':
        camposAdicionais[5] = escapeCSVField(divergencia.statusDoc001 || '');
        camposAdicionais[6] = escapeCSVField(divergencia.situacao || '');
        camposAdicionais[8] = escapeCSVField(divergencia.motivo || '');
        break;
      case 'EXCLUSIVO_VALIDATOR':
        camposAdicionais[7] = escapeCSVField(divergencia.status || '');
        camposAdicionais[8] = escapeCSVField(divergencia.motivo || '');
        break;
      case 'EXCLUSIVO_GFT_LIVRO':
        camposAdicionais[8] = escapeCSVField(divergencia.motivo || '');
        break;
      case 'STATUS_DIVERGENTE':
      case 'STATUS_DIVERGENTE_GFT':
      case 'STATUS_DIVERGENTE_GFT_VALIDATOR':
        camposAdicionais[9] = escapeCSVField(divergencia.tipoDiscrepancia || '');
        camposAdicionais[10] = escapeCSVField(divergencia.gravidade || '');
        camposAdicionais[11] = escapeCSVField(divergencia.detalhes || '');
        break;
    }

    return [tipo, loja, caixa, cupom, migrate, var_, logix, gft, data, ...camposAdicionais];
  };

  const generateCSVContent = (allDivergencias: DivergenciaUnificadaDto[]): string => {
    const lines: string[] = [];

    // Add headers
    const headers = [
      // Colunas principais (visíveis na tabela DivergenciasUnificadasTable)
      'Tipo',
      'Loja',
      'Caixa',
      'Cupom',
      'Migrate',
      'VAR',
      'Logix',
      'GFT',
      'Data',
      // Campos adicionais (específicos por tipo)
      'Cupom Anterior',
      'Próximo Cupom',
      'Tamanho Pulo',
      'Status Simplificado',
      'CNPJ Loja',
      'Status DOC001',
      'Situação',
      'Status',
      'Motivo',
      'Tipo Discrepância',
      'Gravidade',
      'Detalhes'
    ];
    lines.push(headers.join(','));

    // Add data rows
    allDivergencias.forEach(divergencia => {
      lines.push(convertDivergenciaToCSV(divergencia).join(','));
    });

    return lines.join('\n');
  };

  const fetchAllRecords = async (): Promise<DivergenciaUnificadaDto[]> => {
    if (!currentFilters) {
      throw new Error('Filtros não fornecidos para exportação');
    }

    // Set initial progress
    setExportProgress(10);
    setFetchedRecords(0);

    // First request to get total pages
    const firstResponse = await executarConciliacao({
      ...currentFilters,
      page: 1,
      limit: 1000
    }).unwrap();

    if (!firstResponse.divergencias?.divergencias) {
      return [];
    }

    setTotalRecords(firstResponse.paginacao.total);
    const totalPages = firstResponse.paginacao.totalPages;
    const allRecords: DivergenciaUnificadaDto[] = [...firstResponse.divergencias.divergencias];
    setFetchedRecords(allRecords.length);
    setExportProgress(10 + (allRecords.length / firstResponse.paginacao.total) * 80);

    if (totalPages === 1) {
      return allRecords;
    }

    // Fetch remaining pages in parallel batches of 3
    const BATCH_SIZE = 3;
    for (let i = 2; i <= totalPages; i += BATCH_SIZE) {
      const pagesToFetch = [];
      for (let j = i; j < i + BATCH_SIZE && j <= totalPages; j++) {
        pagesToFetch.push(j);
      }

      try {
        const responses = await Promise.all(
          pagesToFetch.map(page =>
            executarConciliacao({
              ...currentFilters,
              page,
              limit: 1000
            }).unwrap()
          )
        );

        responses.forEach(response => {
          if (response.divergencias?.divergencias) {
            allRecords.push(...response.divergencias.divergencias);
          }
        });

        setFetchedRecords(allRecords.length);
        setExportProgress(10 + (allRecords.length / firstResponse.paginacao.total) * 80);

      } catch (error) {
        console.error('Erro ao buscar batch de páginas:', error);
        throw new Error(`Erro ao buscar dados em paralelo`);
      }
    }

    return allRecords;
  };

  const handleExport = async () => {
    if (!currentFilters) {
      alert('Filtros não encontrados. Execute uma conciliação primeiro.');
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
    setFetchedRecords(0);
    setTotalRecords(0);

    try {
      // Fetch all records
      const allRecords = await fetchAllRecords();
      const csvContent = generateCSVContent(allRecords);
      setExportProgress(100);

      // Create and download the file
      const filename = generateFilename();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      // Reset state
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        setFetchedRecords(0);
        setTotalRecords(0);
        onOpenChange(false);
      }, 1000);

    } catch (error) {
      console.error('Erro durante exportação:', error);
      alert(`Erro durante exportação: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      setIsExporting(false);
      setExportProgress(0);
      setFetchedRecords(0);
      setTotalRecords(0);
    }
  };

  const getEstimatedRecords = () => {
    if (isExporting && totalRecords > 0) return totalRecords;
    return data?.paginacao?.total || 0;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Exportar Dados da Conciliação
          </DialogTitle>
          <DialogDescription>
            Exportar todas as divergências encontradas em formato CSV.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Format info */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Formato: CSV (.csv)</span>
            </div>
            <p className="text-xs text-blue-700 mt-1">
              Arquivo otimizado para abrir no Excel ou Google Sheets
            </p>
          </div>

          {/* Warning about fetching all pages */}
          <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded p-3">
            <strong>Atenção:</strong> A exportação irá buscar todas as divergências de todas as páginas.
            O processo pode levar alguns minutos dependendo da quantidade de registros.
          </div>

          {/* Export Progress */}
          {isExporting && (
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Progresso da Exportação</Label>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${exportProgress}%` }}
                ></div>
              </div>
              {totalRecords > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Buscando registros: {fetchedRecords} de {totalRecords} ({exportProgress.toFixed(0)}%)
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Gerando arquivo... {exportProgress.toFixed(0)}%
                </p>
              )}
            </div>
          )}

          {/* File Preview */}
          {!isExporting && (
            <div className="bg-muted/50 rounded-lg p-3">
              <Label className="text-sm font-medium">Arquivo a ser gerado:</Label>
              <p className="text-sm text-muted-foreground mt-1 font-mono">
                {generateFilename()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                ~{getEstimatedRecords()} registro(s) • CSV
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || !currentFilters}
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exportando...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Exportar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}